import { Api, dashboardApiUrl, InterestEarningsParams, walletApiUrl } from "@/services";
import { AutoWithdrawalSettings, BankAccount, DailyWithdrawalStatus, ScheduledPayoutSettings } from "@/types/api";
import { useMutation, UseMutationResult, useQueries, UseQueryResult } from "@tanstack/react-query";
import { createContext, PropsWithChildren, useEffect, useState } from "react";

type WithdrawalForm = {
  amount: number;
  bankAccountId: string;
}

type IWalletContext = {
  wallet: any;
  walletSummary: any;
  weekEarning: any;
  recentPayouts: any[];
  refetchData(): void;
  isLoading: boolean;
  handleChangeEarningInterest(period: InterestEarningsParams["period"]): void;
  interestEarningsQuery?: UseQueryResult<any, Error>
  bankAccountsQuery?: UseQueryResult<{ accounts: BankAccount[] }, Error>
  createBankAccount?: UseMutationResult<any, Error, any, unknown>
  deleteBankAccount?: UseMutationResult<any, Error, string, unknown>
  setPrimaryBankAccount?: UseMutationResult<any, Error, string, unknown>
  walletDashboardQuery?: UseQueryResult<any, Error>
  withdrawalStatus?: DailyWithdrawalStatus
  withdrawal?: UseMutationResult<any, Error, WithdrawalForm, unknown>
  scheduledPayoutQuery?: UseQueryResult<any, Error>
  autoWithdrawalQuery?: UseQueryResult<any, Error>
  configureAutoWithdrawal?: UseMutationResult<any, Error, AutoWithdrawalSettings, unknown>
  configureScheduledPayout?: UseMutationResult<any, Error, ScheduledPayoutSettings, unknown>
}

export const WalletContext = createContext<IWalletContext>({
  wallet: undefined,
  walletSummary: undefined,
  weekEarning: undefined,
  recentPayouts: [],
  refetchData: function (): void {
    throw new Error("Function not implemented.");
  },
  isLoading: false,
  handleChangeEarningInterest: function (period: InterestEarningsParams["period"]): void {
    throw new Error("Function not implemented.");
  },
  interestEarningsQuery: undefined,
  withdrawalStatus: undefined
})

export function WalletProvider({ children }: PropsWithChildren){
  const [walletId, setWalletId] = useState<string>('')
  const [interestEarningsParams, setInterestEarningsParams] = useState<InterestEarningsParams>({period: 'today'})

  function handleChangeEarningInterest(period: InterestEarningsParams["period"]){
    setInterestEarningsParams({
      period
    })
  }

  const queries = useQueries({
    queries: [
      {
        queryKey: ["walletCurrent"],
        queryFn: async () => (await Api.get(walletApiUrl.current)).data?.data
      },
      {
        queryKey: ["transactions"],
        queryFn: async () => (await Api.get(walletApiUrl.transactions)).data?.data
      },
      {
        queryKey: ["summary"],
        queryFn: async () => (await Api.get(walletApiUrl.summary)).data?.data
      },
      {
        queryKey: ["interestEarnings", interestEarningsParams],
        queryFn: async ({ queryKey }) => {
          const [, filters] = queryKey;
          return (await Api.get(walletApiUrl.interestEarnings(walletId, filters as any))).data?.data
        },
        enabled: !!walletId
      },
      {
        queryKey: ["bankAccounts"],
        queryFn: async () => (await Api.get(walletApiUrl.bankAccounts)).data?.data
      },
      {
        queryKey: ["autoWithdrawal"],
        queryFn: async () => (await Api.get(walletApiUrl.autoWithdrawal)).data?.data
      },
      {
        queryKey: ["scheduledPayout"],
        queryFn: async () => (await Api.get(walletApiUrl.scheduledPayout)).data?.data
      },
      {
        queryKey: ["weekEarnings"],
        queryFn: async () => (await Api.get(dashboardApiUrl.earnings)).data?.data?.week
      },
      {
        queryKey: ["walletDashboard"],
        queryFn: async () => (await Api.get(walletApiUrl.dashboard)).data?.data
      },
      {
        queryKey: ["walletStatus"],
        queryFn: async () => (await Api.get(walletApiUrl.status)).data?.data
      },
    ]
  })

  const [walletQuery, transactionsQuery, summaryQuery, interestEarningsQuery, bankAccountsQuery, autoWithdrawalQuery, scheduledPayoutQuery, weekEarningsQuery, walletDashboardQuery, walletStatusQuery] = queries

  const isLoading = walletQuery.isLoading || transactionsQuery.isLoading || summaryQuery.isLoading || bankAccountsQuery.isLoading || autoWithdrawalQuery.isLoading || scheduledPayoutQuery.isLoading || weekEarningsQuery.isLoading

  useEffect(() => {
    if (walletQuery.data) {
      setWalletId(walletQuery.data?.id)
    }
  }, [walletQuery.data])

  function refetchData(){
    queries.forEach(query => query.refetch())
  }

  const createBankAccount = useMutation({
    mutationFn: async (data: any) => (await Api.post(walletApiUrl.bankAccounts, data)).data
  })

  const deleteBankAccount = useMutation({
    mutationFn: async (id: string) => (await Api.delete(walletApiUrl.deleteBankAccount(id))).data
  })

  const setPrimaryBankAccount = useMutation({
    mutationFn: async (id: string) => (await Api.put(walletApiUrl.updateBankAccount(id))).data
  })

  const withdrawal = useMutation({
    mutationFn: async (data: WithdrawalForm) => (await Api.post(walletApiUrl.withdraw, data)).data
  })

  const configureAutoWithdrawal = useMutation({
    mutationFn: async (data: AutoWithdrawalSettings) => (await Api.post(walletApiUrl.autoWithdrawal, data)).data
  })

  const configureScheduledPayout = useMutation({
    mutationFn: async (data: ScheduledPayoutSettings) => (await Api.post(walletApiUrl.scheduledPayout, data)).data
  })

  return (
    <WalletContext.Provider
      value={{
        wallet: walletQuery.data,
        walletSummary: summaryQuery.data?.summary,
        weekEarning: weekEarningsQuery.data,
        recentPayouts: summaryQuery.data?.recentPayouts,
        refetchData,
        isLoading,
        handleChangeEarningInterest,
        interestEarningsQuery,
        bankAccountsQuery,
        createBankAccount,
        deleteBankAccount,
        setPrimaryBankAccount,
        walletDashboardQuery,
        withdrawalStatus: walletStatusQuery.data,
        withdrawal,
        autoWithdrawalQuery,
        scheduledPayoutQuery,
        configureAutoWithdrawal,
        configureScheduledPayout
      }}
    >
      {children}
    </WalletContext.Provider>
  )
}